import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowRightLeft, CalendarClock, ChevronDown, ChevronUp, ExternalLink, Pencil, Plus, RefreshCw, Search, TriangleAlert, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { groupEpisodes, flattenEntries, isEpisodeCard, seriesProgress } from '../lib/longformGroups';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Menu from '../ui/Menu';
import Modal from '../ui/Modal';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import StageChip from '../ui/StageChip';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { cx } from '../ui/util';
import { canSetProducer, filmedHold } from '../lib/filmedHold';

// Live production on src/ui (DESIGN rev 2.1 §3.1: a status board, Grid at high density, grouped by stage).
// Same calls as before: GET /api/live-production (status / excludePosted / includeArchived / editorId), GET /api/clients,
// GET /api/team-members, PATCH /:id/status (row stage change, and the bulk set with force), PATCH /api/tal/lp/:id/notes,
// POST /api/live-production + GET /:id for a riff card. A row opens its production page (P-1).
// Longform (D-120): every episode stays its own row, under one Grid group per series that follows the stage groups.

const LIVE_TRANSITIONS = {
  Scripted:   ['Filming', 'Incomplete', 'Archived'],
  Filming:    ['Filmed',  'Incomplete', 'Archived'],
  Filmed:     ['Editing', 'Incomplete', 'Archived'],
  Editing:    ['Qual',    'Incomplete', 'Archived'],
  Qual:       ['Approved', 'Editing'],
  Approved:   ['Posted'],
  Posted:     ['Approved'],
  Incomplete: ['Filming', 'Archived'],
  Archived:   ['Filming', 'Scripted'],
};
const ALL_STATUSES = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted', 'Incomplete', 'Archived'];
// Stage groups in ladder order. Incomplete is not a rung: it sits in Filming with the Incomplete condition (§2.1).
// Archived (shelved) is no rung either: its own group after Posted, rows muted like parked work.
const STAGE_GROUPS = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted', 'Archived'];
const stageKey = status => (status === 'Incomplete' ? 'Filming' : status);
// The old sortable headers, kept as URL values (sortBy) so stored links keep their sort.
const SORTS = [
  { value: 'Script / Title', label: 'Title' },
  { value: 'Project', label: 'Client' },
  { value: 'Producer', label: 'Shoot lead' },   // OUT-4: the value stays (stored links); the column it sorts is the shoot lead
  { value: 'CardProducer', label: 'Producer' },
  { value: 'Status', label: 'Stage' },
  { value: 'Date', label: 'Shoot date' },
];
// The Talent column's expand-all, remembered per viewer (the old site's header ▾ kept it for the visit only).
const TALENT_EXPANDED_KEY = 'hf-live:talent-expanded';
const TALENT_KIND = {
  booked: 'Booked on a shoot segment',
  mapped: 'Mapping default from the script cast, not booked yet',
  legacy: 'Assigned talent on the card',
  freeform: 'Unstructured legacy talent name',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function shortDate(str) {
  if (!str) return '';
  // Date-only strings parse as a local date so the UTC offset cannot shift the day.
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Row title: an episode reads "Series, Ep N: episode title"; anything else its script name, notes or (Riff). */
function titleOf(r) {
  if (isEpisodeCard(r)) return `${clean(r.script?.scriptName || '(series)')}, Ep ${r.episodeNumber}${r.episodeTitle ? `: ${clean(r.episodeTitle)}` : ''}`;
  return clean(r.script?.scriptName || r.notes || '(Riff)');
}

// Condition, derived from the record only: Incomplete (needs a reshoot), and a cut in Qual waiting on a reviewer.
const conditionOf = r => (r.status === 'Incomplete' ? 'Incomplete' : r.status === 'Qual' ? 'Needs review' : null);

/** The date the current stage is scheduled for: the shoot before it is filmed, the edit while it is in the edit. */
function dueOf(r) {
  if (['Scripted', 'Filming', 'Incomplete'].includes(r.status) && r.shootScheduledDate && !r.dateFilmed) return { what: 'Shoot', date: r.shootScheduledDate };
  if (['Filmed', 'Editing'].includes(r.status) && r.editScheduledDate) return { what: 'Edit', date: r.editScheduledDate };
  return null;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LiveProduction() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = !user?.isExternal;

  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  const [search,       setSearch]       = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  // Posted and All views only: include cards that were filed away with Archive.
  const [showArchived, setShowArchived] = useState(false);
  const [talentExpanded, setTalentExpanded] = useState(() => { try { return localStorage.getItem(TALENT_EXPANDED_KEY) === '1'; } catch { return false; } });
  const [clientFilter, setClientFilter] = useState(() => searchParams.get('client') || '');
  const [editorFilter, setEditorFilter] = useState(() => searchParams.get('editor') || '');
  const [liveSort,     setLiveSort]     = useState(() => ({ col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' }));
  const [clients,      setClients]      = useState([]);
  const [members,      setMembers]      = useState([]);
  const [showRiff,     setShowRiff]     = useState(false);
  const [menuFor,      setMenuFor]      = useState(null);   // row id whose stage menu is open
  const [notesFor,     setNotesFor]     = useState(null);   // row id whose notes editor is open
  const [busyId,       setBusyId]       = useState(null);

  // Bulk selection (owner order 2026-09-01): tick rows, then set them all to one status. Internal users only.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkStatus,  setBulkStatus]  = useState('');
  const [bulkBusy,    setBulkBusy]    = useState(false);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      // D-057: the client filter is applied in the page, so its options come from the rows in the view.
      if (editorFilter) params.set('editorId', editorFilter);
      if (statusFilter === 'Posted') {
        params.set('status', 'Posted');
        if (showArchived) params.set('includeArchived', 'true');
      } else if (statusFilter === 'ALL') {
        if (showArchived) params.set('includeArchived', 'true');
      } else if (statusFilter) {
        params.set('status', statusFilter);
      } else {
        // Default: hide Posted (finished) and Archived (shelved); both have their own views.
        params.set('excludePosted', 'true');
      }
      const res = await api.get(`/api/live-production?${params}`);
      setRecords(res.data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, editorFilter, showArchived]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // P-1: /live?card=<id> (stored links, notifications, old "Copy link") redirects to the production page, once.
  useEffect(() => {
    const cardId = searchParams.get('card');
    if (!cardId) return;
    const p = new URLSearchParams(searchParams); p.delete('card');
    const rest = p.toString();
    navigate(`/production/${cardId}${rest ? `?${rest}` : ''}`, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link through navigation state (notification, Edit queue): open that production page.
  useEffect(() => {
    const openId = location.state?.openRecordId;
    if (!openId) return;
    navigate(`/production/${openId}`, { replace: true });
  }, [location.state, navigate]);

  // Filters and sort live in the URL so the back button restores them (not while a ?card= redirect is in flight).
  useEffect(() => {
    if (searchParams.get('card')) return;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (editorFilter) p.set('editor', editorFilter); else p.delete('editor');
      if (liveSort.col) p.set('sortBy', liveSort.col); else p.delete('sortBy');
      if (liveSort.dir !== 'asc') p.set('sortDir', liveSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
  }, [liveSort, search, statusFilter, clientFilter, editorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // A row that leaves the view leaves the selection too: a hidden row is never mass-changed.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(records.map(r => r.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [records]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const statusCounts = useMemo(() => {
    const c = {};
    records.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [records]);

  // D-057: client options = clients with at least one row in the current view (plus the current pick).
  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const r of records) if (r.client && !seen.has(r.client.id)) seen.set(r.client.id, r.client);
    if (clientFilter && !seen.has(clientFilter)) {
      const known = clients.find(c => c.id === clientFilter);
      if (known) seen.set(known.id, known);
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [records, clientFilter, clients]);

  const editorOptions = useMemo(() => members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User')), [members]);
  // OUT-4 (C5 §4): the producer picker lists Producer-role members (the Filmed board's people); Admin, Producer, Power User
  // and Manager may set it (the API's gate).
  const producerOptions = useMemo(() => members.filter(m => m.isActive !== false && !m.isExternal && m.roles?.some(r => r.role === 'Producer')), [members]);
  const canPickProducer = canSetProducer(user);

  const filtered = useMemo(() => {
    let out = records;
    if (clientFilter) out = out.filter(r => r.client?.id === clientFilter);
    if (!search) return out;
    const q = search.toLowerCase();
    // §4.3: search matches the resolved talent names the Talent column shows; file_name stays searchable.
    return out.filter(r =>
      r.script?.scriptName?.toLowerCase().includes(q) ||
      r.episodeTitle?.toLowerCase().includes(q) || (r.episodeNumber != null && `ep ${r.episodeNumber}`.includes(q)) ||
      r.actorName?.toLowerCase().includes(q) ||
      (r.resolvedTalent?.talents || []).some(t => t.name?.toLowerCase().includes(q)) ||
      r.notes?.toLowerCase().includes(q) ||
      r.fileName?.toLowerCase().includes(q) ||
      r.client?.name?.toLowerCase().includes(q));
  }, [records, search, clientFilter]);

  const sorted = useMemo(() => {
    if (!liveSort.col) return filtered;
    const { col, dir } = liveSort;
    return [...filtered].sort((a, b) => {
      let av = '', bv = '';
      if      (col === 'Script / Title') { av = a.script?.scriptName || a.notes || ''; bv = b.script?.scriptName || b.notes || ''; }
      else if (col === 'Project')        { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (col === 'Producer')       { av = a.shootLead?.name || ''; bv = b.shootLead?.name || ''; }
      else if (col === 'CardProducer')   { av = a.producer?.name || ''; bv = b.producer?.name || ''; }
      else if (col === 'Status')         { av = a.status || ''; bv = b.status || ''; }
      else if (col === 'Date')           { av = a.dateFilmed || a.shootScheduledDate || ''; bv = b.dateFilmed || b.shootScheduledDate || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, liveSort]);

  // Series progress counts every episode the grid loaded for that series, not only the filtered ones (D-120).
  const allEpisodesBySeries = useMemo(() => {
    const m = new Map();
    for (const r of records) if (isEpisodeCard(r)) { const k = r.scriptId || r.script?.id; if (!m.has(k)) m.set(k, []); m.get(k).push(r); }
    return m;
  }, [records]);

  const { rows, groups, groupOf } = useMemo(() => {
    const entries = groupEpisodes(sorted);
    const present = new Set(entries.filter(e => e.kind === 'card').map(e => stageKey(e.record.status)));
    const stageDefs = STAGE_GROUPS.filter(s => present.has(s)).map(s => ({
      key: s, header: s === 'Archived' ? 'Archived' : <StageChip stage={s} />,
    }));
    const seriesDefs = entries.filter(e => e.kind === 'series').map(e => {
      const all = allEpisodesBySeries.get(e.scriptId) || e.episodes;
      const progress = seriesProgress(all);
      const shown = e.episodes.length;
      return {
        key: e.key,
        header: (
          <>
            {clean(e.scriptName)}
            <Tag>Longform</Tag>
            <span className="ui-meta">{clean(progress.label)}{shown !== all.length ? `, ${shown} of ${all.length} match` : ''}</span>
          </>
        ),
      };
    });
    return {
      rows: flattenEntries(entries),
      groups: [...stageDefs, ...seriesDefs],
      groupOf: r => (isEpisodeCard(r) ? `series:${r.scriptId || r.script?.id}` : stageKey(r.status)),
    };
  }, [sorted, allEpisodesBySeries]);

  const incompleteCount = statusCounts.Incomplete || 0;
  const filtersOn = !!(search || statusFilter || clientFilter || editorFilter);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setClientFilter(''); setEditorFilter(''); };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const openProduction = useCallback(r => navigate(`/production/${r.id}`), [navigate]);

  async function handleRowStatusChange(record, newStatus) {
    setMenuFor(null);
    const held = filmedHold(record, newStatus, user);
    if (held) { toast(`${held}.`, { tone: 'warn' }); return; }
    setBusyId(record.id);
    try {
      const res = await api.patch(`/api/live-production/${record.id}/status`, { status: newStatus, changedBy: user.id });
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, ...res.data } : r)));
      toast(`${titleOf(record)} moved to ${newStatus}.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  // OUT-4: set a card's producer from the board (PATCH /api/live-production/:id { producerId }; the same id confirms a
  // defaulted one, ruling K).
  async function handleProducerSet(record, member) {
    setBusyId(record.id);
    try {
      const res = await api.patch(`/api/live-production/${record.id}`, { producerId: member.id });
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, producerId: member.id, producer: res.data.producer || { id: member.id, name: member.name }, producerDefaulted: false } : r)));
      toast(record.producerId === member.id ? `${clean(member.name)} confirmed as producer of ${titleOf(record)}.` : `${clean(member.name)} is the producer of ${titleOf(record)}.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The producer was not set. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  // §4.5: the same audited endpoint as the Script list. On a riff card the notes field IS its title.
  async function handleNotesSave(record, value) {
    if (value === (record.notes || '')) return true;
    try {
      await api.patch(`/api/tal/lp/${record.id}/notes`, { notes: value });
      setRecords(prev => prev.map(r => (r.id === record.id ? { ...r, notes: value } : r)));
      return true;
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The notes did not save. Try again.', { tone: 'danger' });
      return false;
    }
  }

  async function handleBulkStatus() {
    const ids = [...selectedIds];
    if (!bulkStatus || ids.length === 0 || bulkBusy) return;
    const ok = await confirm({
      title: `Set ${plural(ids.length, 'production')} to ${bulkStatus}?`,
      body: 'This jumps straight to that stage, with no pipeline steps in between. A card your role cannot move is skipped.',
      confirmLabel: `Set to ${bulkStatus}`,
    });
    if (!ok) return;
    setBulkBusy(true);
    let changed = 0;
    const skipped = [];
    for (const id of ids) {
      // OUT-4: a move that would mark a card filmed waits for its producer, as one card does
      const rec = records.find(r => r.id === id);
      const held = filmedHold(rec, bulkStatus, user);
      if (held) { skipped.push(`${titleOf(rec)} (${held.toLowerCase()})`); continue; }
      try {
        // force: bulk is a direct set (owner call 2026-09-01); role gates still apply per row.
        await api.patch(`/api/live-production/${id}/status`, { status: bulkStatus, changedBy: user.id, force: true });
        changed++;
      } catch (e) {
        skipped.push(`${rec ? titleOf(rec) : id} (${clean(e.response?.data?.error) || 'failed'})`);
      }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    setBulkStatus('');
    fetchRecords();
    const head = `${changed} of ${plural(ids.length, 'production')} set to ${bulkStatus}.`;
    const tail = skipped.length ? ` Skipped ${skipped.length}: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? `, and ${skipped.length - 3} more` : ''}.` : '';
    toast(head + tail, { tone: skipped.length ? 'warn' : 'ok', duration: skipped.length ? 12000 : 6000 });
  }

  const toggleSelect = id => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.id));

  // ── Header ──────────────────────────────────────────────────────────────────

  usePageHeader({
    title: 'Live production',
    meta: loading ? 'Loading' : `${plural(records.length, 'production')}, ${incompleteCount} incomplete`,
    actions: (
      <>
        {incompleteCount > 0 && statusFilter !== 'Incomplete' && (
          <Button variant="quiet" icon={TriangleAlert} onClick={() => setStatusFilter('Incomplete')}>Show incomplete</Button>
        )}
        <Button variant="quiet" icon={RefreshCw} onClick={fetchRecords}>Refresh</Button>
        {canEdit && <Button variant="primary" icon={Plus} onClick={() => setShowRiff(true)}>New riff card</Button>}
      </>
    ),
    filters: (
      <>
        <Input icon={Search} aria-label="Search by title, talent or client" placeholder="Search by title, talent or client"
          value={search} onChange={e => setSearch(e.target.value)} />
        <Select aria-label="Stage" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'Active (hides Posted)' },
            { value: 'ALL', label: 'All, including Posted' },
            ...ALL_STATUSES.map(s => ({ value: s, label: statusCounts[s] != null ? `${s} (${statusCounts[s]})` : s })),
          ]} />
        {(statusFilter === 'Posted' || statusFilter === 'ALL') && (
          <Checkbox label="Show archived" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
        )}
        <Select aria-label="Client" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
          options={[{ value: '', label: 'All clients' }, ...clientOptions.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        {canEdit && (
          <Select aria-label="Editor" value={editorFilter} onChange={e => setEditorFilter(e.target.value)}
            options={[{ value: '', label: 'All editors' }, ...editorOptions.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        )}
        <Select aria-label="Sort" value={liveSort.col} onChange={e => setLiveSort(s => ({ ...s, col: e.target.value }))}
          options={[{ value: '', label: 'Newest first' }, ...SORTS.filter(s => canEdit || !['Producer', 'CardProducer'].includes(s.value)).map(s => ({ value: s.value, label: `Sort by ${s.label.toLowerCase()}` }))]} />
        {liveSort.col && (
          <Button variant="quiet" iconOnly icon={liveSort.dir === 'asc' ? ArrowUp : ArrowDown}
            label={liveSort.dir === 'asc' ? 'Ascending, switch to descending' : 'Descending, switch to ascending'}
            onClick={() => setLiveSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))} />
        )}
        {filtersOn && <Button variant="quiet" icon={X} onClick={clearFilters}>Clear</Button>}
      </>
    ),
  });

  // ── Grid ────────────────────────────────────────────────────────────────────

  const toggleTalent = () => setTalentExpanded(v => {
    try { localStorage.setItem(TALENT_EXPANDED_KEY, v ? '0' : '1'); } catch { /* storage off: the toggle still works for this visit */ }
    return !v;
  });

  const columns = [
    ...(canEdit ? [{
      key: 'select', width: 56,
      label: <Checkbox label={<span className="ui-sr">Select every row in the view</span>} checked={allSelected}
        onChange={e => setSelectedIds(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} />,
      render: r => (
        <span onClick={e => e.stopPropagation()}>
          <Checkbox label={<span className="ui-sr">Select {titleOf(r)}</span>} checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} />
        </span>
      ),
    }] : []),
    { key: 'title', label: 'Title', width: 300, render: r => <TitleCell record={r} /> },
    { key: 'stage', label: 'Stage', width: 124, render: r => (r.status === 'Archived' ? <span className="ui-meta">Archived</span> : <StageChip stage={stageKey(r.status)} />) },
    { key: 'condition', label: 'Condition', width: 132, render: r => (conditionOf(r) ? <Badge condition={conditionOf(r)} /> : null) },
    { key: 'client', label: 'Client', width: 150, render: r => (r.client?.name ? <Tag>{clean(r.client.name)}</Tag> : <span className="ui-meta">No client</span>) },
    // OUT-4: Producer beside Editor, ahead of Talent: at the right edge the row's hover actions covered the picker
    ...(canEdit ? [
      { key: 'producer', label: 'Producer', width: 140, render: r => (
        <ProducerCell record={r} options={producerOptions} canPick={canPickProducer} busy={busyId === r.id} onPick={m => handleProducerSet(r, m)} />
      ) },
      { key: 'editor', label: 'Editor', width: 130, render: r => (r.editor?.name ? clean(r.editor.name) : <span className="ui-meta">Unassigned</span>) },
    ] : []),
    { key: 'talent', width: 180, render: r => <TalentCell record={r} expanded={talentExpanded} />,
      label: (
        <span className="ui-th-toggle">Talent
          <Button variant="quiet" iconOnly icon={talentExpanded ? ChevronUp : ChevronDown} aria-expanded={talentExpanded}
            label={talentExpanded ? 'Collapse the Talent column to one line per row' : 'Expand the Talent column to show every row\'s full cast'}
            onClick={toggleTalent} />
        </span>
      ) },
    { key: 'due', label: 'Due', width: 104, render: r => { const d = dueOf(r); return d ? `${d.what} ${shortDate(d.date)}` : null; } },
    { key: 'updated', label: 'Updated', width: 88, render: r => shortDate(r.updatedAt) },
    ...(canEdit ? [
      { key: 'shootlead', label: 'Shoot lead', width: 130, render: r => (r.shootLead?.name ? clean(r.shootLead.name) : <span className="ui-meta">Unassigned</span>) },
    ] : []),
    { key: 'shoot', label: 'Shoot date', width: 96, render: r => shortDate(r.dateFilmed || r.shootScheduledDate) || null },
    { key: 'posted', label: 'Posted', width: 88, render: r => shortDate(r.postedDate) || null },
    ...(canEdit ? [{ key: 'notes', label: 'Notes', width: 220, render: r => (r.scriptId && r.notes ? clean(r.notes) : null) }] : []),
  ];

  const rowActions = r => {
    const title = titleOf(r);
    const transitions = LIVE_TRANSITIONS[r.status] || [];
    return (
      <>
        {canEdit && r.status === 'Incomplete' && (
          <Button variant="quiet" iconOnly icon={CalendarClock} label={`Schedule a reshoot for ${title}`} onClick={() => openProduction(r)} />
        )}
        {canEdit && transitions.length > 0 && (
          <Popover open={menuFor === r.id} onOpenChange={o => setMenuFor(o ? r.id : null)} align="end" label={`Change the stage of ${title}`}
            trigger={({ toggle, ref, props }) => (
              <Button ref={ref} variant="quiet" iconOnly icon={ArrowRightLeft} label={`Change the stage of ${title}`} loading={busyId === r.id} onClick={toggle} {...props} />
            )}>
            <Menu label={`Move ${title} to`} onClose={() => setMenuFor(null)}
              items={transitions.map(t => { const held = filmedHold(r, t, user);
                return { label: held ? `Move to ${t}: ${held.toLowerCase()}` : `Move to ${t}`, disabled: !!held, title: held || undefined, onSelect: () => handleRowStatusChange(r, t) }; })} />
          </Popover>
        )}
        {canEdit && (
          <NotesEditor record={r} open={notesFor === r.id} onOpenChange={o => setNotesFor(o ? r.id : null)} onSave={handleNotesSave} />
        )}
        <a className="ui ui-btn ui-btn--quiet ui-btn--icon" href={`/production/${r.id}`} target="_blank" rel="noreferrer"
          aria-label={`Open ${title} in a new tab`} title="Open in a new tab">
          <Icon icon={ExternalLink} />
        </a>
      </>
    );
  };

  return (
    <div className="ui ui-page ui-page--flush">
      {canEdit && selectedIds.size > 0 && (
        <div className="ui-inset ui-inset--bar">
          <div className="ui-toolbar" role="group" aria-label="Bulk stage change">
            <span className="ui-meta">{selectedIds.size} selected</span>
            <Select aria-label="Set stage to" value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              placeholder="Set stage to" options={ALL_STATUSES} />
            <Button variant="primary" loading={bulkBusy} disabled={!bulkStatus} onClick={handleBulkStatus}>Apply to {selectedIds.size}</Button>
            <Button variant="quiet" disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}>Clear selection</Button>
          </div>
        </div>
      )}
      <div className="ui-work">
        <Grid
          label="Live production"
          columns={columns} rows={rows} rowKey={r => r.id} groups={groups} groupOf={groupOf}
          density="high" storageKey="hf-grid:live" autoRows={talentExpanded}
          onOpen={openProduction} rowActions={rowActions} mutedRow={r => r.status === 'Archived'}
          state={loadError ? 'error' : loading ? 'loading' : 'data'}
          empty={filtersOn
            ? { message: 'No production matches these filters. Clear them to see every active production.', action: { label: 'Clear filters', icon: X, onClick: clearFilters } }
            : canEdit
              ? { message: 'No production is active. Approve a script to give it a production card, or add a riff card.', action: { label: 'New riff card', icon: Plus, onClick: () => setShowRiff(true) } }
              : { message: 'No production is active for your projects yet.' }}
          error={{ title: 'Live production did not load', message: 'Check your connection, then try again.', onRetry: fetchRecords }} />
      </div>

      {canEdit && (
        <RiffModal open={showRiff} clients={clients} members={members} onClose={() => setShowRiff(false)}
          onCreated={record => {
            setRecords(prev => [record, ...prev]);
            setShowRiff(false);
            toast(`Riff card ${clean(record.notes || '')} created.`);
          }} />
      )}
    </div>
  );
}

// ── Cells ─────────────────────────────────────────────────────────────────────

// OUT-4 (C5 §4): the card's producer. Read-only text for most roles; for the gate (Admin, Producer, Power User, Manager)
// a menu of Producer-role members. A defaulted producer (ruling K) reads tertiary until confirmed; its menu offers Confirm.
function ProducerCell({ record: r, options, canPick, busy, onPick }) {
  const [open, setOpen] = useState(false);
  const name = r.producer?.name ? clean(r.producer.name) : null;
  const defaulted = !!(r.producerDefaulted && name);
  const face = name
    ? <span className={defaulted ? 'ui-text-3' : undefined} title={defaulted ? `${name}, defaulted when the card was marked filmed` : name}>{name}</span>
    : <span className="ui-meta">Unassigned</span>;
  if (!canPick) return face;
  const contain = e => { if (e.key !== 'Escape') e.stopPropagation(); };
  const current = options.find(m => m.id === r.producerId);
  const items = [
    ...(defaulted && current ? [{ label: `Confirm ${clean(current.name)}`, onSelect: () => onPick(current) }, { separator: true }] : []),
    ...options.filter(m => m.id !== r.producerId).map(m => ({ label: clean(m.name), onSelect: () => onPick(m) })),
  ];
  return (
    <span className="ui-cellmenu" onClick={e => e.stopPropagation()} onKeyDown={contain}>
      <Popover open={open} onOpenChange={setOpen} label={`Producer of ${titleOf(r)}`}
        trigger={({ toggle, ref, props }) => (
          <button ref={ref} type="button" className="ui-cellmenu__btn" onClick={toggle} disabled={busy} {...props}
            aria-label={`Producer: ${name || 'unassigned'}${defaulted ? ', defaulted' : ''}. Change`} data-testid="producer-cell">
            <span className="ui-cellmenu__first">{face}</span>
            <Icon icon={ChevronDown} />
          </button>
        )}>
        <Menu label={`Producer of ${titleOf(r)}`} onClose={() => setOpen(false)}
          items={items.length ? items : [{ label: 'No Producer-role members', disabled: true }]} />
      </Popover>
    </span>
  );
}

function TitleCell({ record: r }) {
  const isRiff = !r.scriptId;
  const phase1 = r.isCooking && r.cookingPhase === 2 ? r.cookingPhase1?.script?.scriptName : null;
  return (
    <>
      {titleOf(r)}
      {isRiff && <> <Tag>Riff</Tag></>}
      {r.isCooking && <> <Tag title={phase1 ? `Cooking phase 2 of ${clean(phase1)}` : r.cookingPhase === 1 ? 'Cooking phase 1, recipe shoot' : 'Cooking phase 2, talking head'}>Cooking P{r.cookingPhase || ''}</Tag></>}
      {r.isArchived && <> <Tag title="Filed away after Posted">Archived</Tag></>}
    </>
  );
}

// §4.2: derived, multi-valued. Booked and legacy names read solid; a mapping default or a freeform name reads tertiary.
// Collapsed, the cell shows the first name and "+N" and its menu lists the whole cast; with the column expanded
// (the header toggle, as on the old site) every row shows its whole cast, wrapped.
function TalentCell({ record: r, expanded }) {
  const [open, setOpen] = useState(false);
  const rt = r.resolvedTalent || { kind: 'none', talents: [] };
  if (rt.kind === 'none' || rt.talents.length === 0) return <span className="ui-meta">No talent</span>;
  const names = rt.talents.map(t => clean(t.name));
  const kind = TALENT_KIND[rt.kind];
  const tone = rt.kind === 'mapped' || rt.kind === 'freeform' ? 'ui-text-3' : undefined;
  if (expanded) return <span className={cx('ui-cell-lines', tone)} title={kind}>{names.join(', ')}</span>;
  if (names.length === 1) return <span className={tone} title={kind ? `${names[0]}: ${kind}` : names[0]}>{names[0]}</span>;
  // The row opens on click and moves on arrow keys; neither may leak out of the menu. Escape still reaches the Popover.
  const contain = e => { if (e.key !== 'Escape') e.stopPropagation(); };
  return (
    <span className={cx('ui-cellmenu', tone)} onClick={e => e.stopPropagation()} onKeyDown={contain}>
      <Popover open={open} onOpenChange={setOpen} label={`Cast of ${titleOf(r)}`}
        trigger={({ toggle, ref, props }) => (
          <button ref={ref} type="button" className="ui-cellmenu__btn" onClick={toggle} {...props}
            aria-label={`Talent: ${names.join(', ')}`} title={`${names.join(', ')}${kind ? `: ${kind}` : ''}`}>
            <span className="ui-cellmenu__first">{names[0].split(' ')[0]}</span>
            <span className="ui-cellmenu__more">+{names.length - 1}</span>
            <Icon icon={ChevronDown} />
          </button>
        )}>
        <Menu label={`Cast of ${titleOf(r)}`} onClose={() => setOpen(false)}
          items={names.map(n => ({ label: n, title: kind }))} />
      </Popover>
    </span>
  );
}

// ── Notes editor (row action) ─────────────────────────────────────────────────

// Enter or Save saves, a click elsewhere saves (the old cell saved on blur), Escape discards.
function NotesEditor({ record, open, onOpenChange, onSave }) {
  const isRiff = !record.scriptId;
  const [value, setValue] = useState(record.notes || '');
  const [saving, setSaving] = useState(false);
  const cancelled = useRef(false);
  const label = isRiff ? `Rename ${titleOf(record)}` : `Edit the notes of ${titleOf(record)}`;

  useEffect(() => { if (open) { setValue(record.notes || ''); cancelled.current = false; } }, [open, record.notes]);

  async function save() {
    setSaving(true);
    const ok = await onSave(record, value);
    setSaving(false);
    if (ok) onOpenChange(false);
  }

  return (
    <Popover open={open} align="end" label={label}
      onOpenChange={o => {
        if (o) { onOpenChange(true); return; }
        if (cancelled.current) { onOpenChange(false); return; }
        save();
      }}
      trigger={({ toggle, ref, props }) => (
        <Button ref={ref} variant="quiet" iconOnly icon={Pencil} label={label} onClick={toggle} {...props} />
      )}>
      <div className="ui-popbody">
        <Input label={isRiff ? 'Riff card title' : 'Notes'} hint={isRiff ? 'A riff card keeps its title in this field.' : 'The same notes as on the production page.'}
          value={value} autoFocus onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') cancelled.current = true;
          }} />
        <div className="ui-cluster ui-cluster--end">
          <Button variant="quiet" onClick={() => { cancelled.current = true; onOpenChange(false); }}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={save}>Save</Button>
        </div>
      </div>
    </Popover>
  );
}

// ── New riff card ─────────────────────────────────────────────────────────────

function RiffModal({ open, clients, members, onClose, onCreated }) {
  const [clientId,    setClientId]    = useState('');
  const [title,       setTitle]       = useState('');
  const [actorName,   setActorName]   = useState('');
  const [shootLeadId, setShootLeadId] = useState('');
  const [editorId,    setEditorId]    = useState('');
  const [saving,      setSaving]      = useState(false);
  const [errors,      setErrors]      = useState({});

  useEffect(() => {
    if (!open) return;
    setClientId(''); setTitle(''); setActorName(''); setShootLeadId(''); setEditorId(''); setSaving(false); setErrors({});
  }, [open]);

  // T-3: the Producer list is Producer-role members only (internal, active).
  const shootLeads = members.filter(m => m.isActive !== false && !m.isExternal && m.roles?.some(r => r.role === 'Producer'));
  const editors    = members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'));

  async function handleCreate() {
    const next = {};
    if (!clientId) next.client = 'Choose the client this riff card is for.';
    if (!title.trim()) next.title = 'Give the riff card a title.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      const res = await api.post('/api/live-production', {
        clientId,
        actorName:   actorName   || null,
        shootLeadId: shootLeadId || null,
        editorId:    editorId    || null,
        notes:       title.trim(),
        status:      'Scripted',
      });
      // The created record with its relations, as the grid shows it.
      const detail = await api.get(`/api/live-production/${res.data.id}`);
      onCreated(detail.data);
    } catch (err) {
      setErrors({ form: clean(err.response?.data?.error) || 'The riff card was not created. Try again.' });
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New riff card"
      footer={(
        <>
          <Button variant="quiet" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleCreate}>Create riff card</Button>
        </>
      )}>
      <div className="ui-stack">
        <p className="ui-p ui-dense ui-text-2">A production without a script, for riff or freestyle content.</p>
        <Select label="Client" value={clientId} onChange={e => setClientId(e.target.value)} error={errors.client}
          placeholder="Choose a client" options={clients.map(c => ({ value: c.id, label: clean(c.name) }))} />
        <Input label="Title" value={title} onChange={e => setTitle(e.target.value)} error={errors.title}
          placeholder="For example DD-Animated Braces" data-autofocus />
        <Input label="Talent" value={actorName} onChange={e => setActorName(e.target.value)} placeholder="Optional" />
        <Select label="Producer" value={shootLeadId} onChange={e => setShootLeadId(e.target.value)}
          placeholder="Not assigned" options={shootLeads.map(m => ({ value: m.id, label: clean(m.name) }))} />
        <Select label="Editor" value={editorId} onChange={e => setEditorId(e.target.value)}
          placeholder="Not assigned" options={editors.map(m => ({ value: m.id, label: clean(m.name) }))} />
        {errors.form && <p className="ui-p ui-field__error" role="alert">{errors.form}</p>}
      </div>
    </Modal>
  );
}
